import { Location } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { AlertService, Member, RestService, UiComponent, YesNo } from "@recore-solutions/nstar-lib";
import { Cacheable, CacheBuster } from "ts-cacheable";
import { EMPTY, Observable, of, PartialObserver } from "rxjs";
import { catchError, map, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import * as CacheBusters from "../../shared/cache-busters";
import { ApiPayload } from "../interfaces/api-payload";
import { ApiResponse } from "../interfaces/api-response";
import { Listing } from "../interfaces/listing";
import { ListingPropertyTax } from "../interfaces/listing-property-tax";
import { ListingStatusCD } from "../interfaces/listing-status";
import { getListingSummaryPropertyTypeCD, ListingSummary } from "../interfaces/listing-summary";
import { FK } from "../interfaces/types";
import { ListingSummaryService } from "./listing-summary.service";
import { rewriteApplicationUIComponents } from "./rewriteApplicationUiComponents";
import { LoonEnvironmentName } from "src/environments/loon.environment";

interface RelatedRecord {
	key: string;
	newProperty: string;
	params: any[];
	recordType: string;
	returnKey: string;
}
@Injectable({
	providedIn: "root",
})
export class ListingService {
	listingUrl = environment.endpoints.listing;
	uiComponentsUrl = environment.endpoints.uiComponents;
	upsertListingsUrl = environment.endpoints.upsertListings;
	specialUpsertListingsUrl = environment.endpoints.specialUpsertListings;
	timestampListingUrl = environment.endpoints.timestampListing;
	searchingMemberUrl = environment.endpoints.searchingMember;
	dynamicDataUrl = environment.endpoints.dynamicData;
	genericProcUrl = environment.endpoints.genericProc;
	listingSummaryUrl = environment.endpoints.listingSummary;
	taxDataUrl = environment.endpoints.taxData;
	listingOperationsUrl = environment.endpoints.listingOperations;
	addEditMediaUrl = environment.endpoints.addEditMedia;
	autopopUrl = environment.endpoints.autopop;
	memberUrl = environment.endpoints.member.get;
	listingCityUrl = environment.endpoints.listingCity;

	constructor(
		private rest: RestService,
		private router: Router,
		private location: Location,
		private alertService: AlertService,
		private listingSummaryService: ListingSummaryService
	) {}

	resolve(
		activatedRouteSnapshot: ActivatedRouteSnapshot,
		routerStateSnapshot: RouterStateSnapshot
	): Observable<ListingSummary> {
		const deletedListing = (listingSummary: ListingSummary) => {
			if (listingSummary.listingStatusCD === "D") {
				throw new Error("Cannot navigate to deleted listing!");
			}
		};
		const redirect = () => {
			this.router
				.navigate(["error/404"], { skipLocationChange: true })
				.then(() => this.location.go(routerStateSnapshot.url));
			return EMPTY;
		};

		const listingID = activatedRouteSnapshot.params.mlid;
		const inStore$ = this.listingSummaryService.collection$.pipe(
			map((collection) => collection.entities[listingID]),
			take(1)
		);
		const inCloud$ = this.listingSummaryService
			.getWithQuery({
				queryString: `Listing_ID=${listingID}`,
			})
			.pipe(map((listingSummaries) => listingSummaries[0]));
		const listingSummary$ = inStore$.pipe(
			switchMap((storeListingSummary) =>
				!!storeListingSummary ? of(storeListingSummary) : inCloud$
			)
		);
		return listingSummary$.pipe(tap(deletedListing), catchError(redirect));
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingCacheBuster$,
		maxAge: 60 * 1000,
		maxCacheCount: 99,
	})
	getListingApiResponse(listingSK: number): Observable<ApiResponse<Listing, any[]>> {
		const httpParams = new HttpParams({ fromObject: { listingSK: listingSK.toString() } });
		return this.rest.get<ApiResponse<Listing, any[]>>([this.listingUrl], httpParams);
	}

	@Cacheable({
		maxAge: 60 * 60 * 1000,
		maxCacheCount: 99,
		cacheHasher: ([listingSummary, longName]: [ListingSummary, string]) =>
			longName
				? `${listingSummary.propertyTypeCD}:${listingSummary.listingOrganizationSK}:${longName}`
				: `${listingSummary.propertyTypeCD}:${listingSummary.listingOrganizationSK}`,
	})
	getListingUiComponents(
		listingSummary: ListingSummary,
		longName: string = "Listing Detail"
	): Observable<UiComponent<string>[]> {
		const httpParams = new HttpParams({
			fromObject: {
				applicationName: "Loon",
				longName,
				applicationType_CD: getListingSummaryPropertyTypeCD(listingSummary),
				organization_SK: listingSummary.listingOrganizationSK,
			},
		});
		return this.rest.get<UiComponent<string>[]>([this.uiComponentsUrl], httpParams).pipe(
			map((components) => (components || []).sort((a, b) => a.sortOrder - b.sortOrder)),
			map(rewriteApplicationUIComponents),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingCacheBuster$,
		maxAge: 60 * 1000,
		maxCacheCount: 99,
	})
	getListingTables(listingSK: number): Observable<{ [table: string]: any[] }> {
		return this.getListingApiResponse(listingSK).pipe(map(this.mapListingApiResponseToTables));
	}

	mapListingApiResponseToTables = (
		response: ApiResponse<Listing, any[]>
	): { [table: string]: any[] } => {
		return {
			...this.mapRelatedRecordsToTables(response.related),
			[response.recordType]: response.records,
		};
	};

	codeTableSortFn =
		(tableName: string) =>
		(a: { sortOrder?: number }, b: { sortOrder?: number }): number => {
			if (a.hasOwnProperty("sortOrder") || b.hasOwnProperty("sortOrder")) {
				return (a?.sortOrder || 0) - (b?.sortOrder || 0);
			}
			const nameColumn = this.toCamelCase(tableName) + "Name";
			if (a?.hasOwnProperty(nameColumn) || b?.hasOwnProperty(nameColumn)) {
				return ((a?.[nameColumn] || "") as string).localeCompare(b?.[nameColumn] || "");
			}
			return 0;
		};

	saveListing(payload: ApiPayload<Listing, [any]>): Observable<any> {
		return this.upsertListing(payload, false);
	}

	specialSaveListing(payload: ApiPayload<Listing, [any]>): Observable<any> {
		return this.upsertListing(payload, true);
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingCacheBuster$,
	})
	upsertListing(
		payload: ApiPayload<Listing, any[]>,
		special: boolean
	): Observable<ApiResponse<Listing, any[]>> {
		const url = special ? this.specialUpsertListingsUrl : this.upsertListingsUrl;
		const apiResponse = this.rest.post<ApiResponse<Listing, any[]>>(url, payload);
		CacheBusters.listingCacheBuster$.next();
		return apiResponse;
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingCacheBuster$,
	})
	timestampListing(listingSK: number): Observable<ApiResponse<Listing, any[]>> {
		const url = `${this.timestampListingUrl}/${listingSK}`;
		return this.rest.post<ApiResponse<Listing, any[]>>(url);
	}

	mapRelatedRecordsToTables(relatedRecords: ApiResponse<unknown, any[]>["related"]): {
		[table: string]: any[];
	} {
		return (relatedRecords || []).reduce(this.reduceRelatedRecordToTables, {});
	}

	reduceRelatedRecordToTables = (
		tables: { [table: string]: any[] },
		relatedRecord: ApiResponse<any>
	): { [table: string]: any[] } => {
		if (relatedRecord.recordType.indexOf("_") > -1) {
			return { ...tables, ...this.mapMetaTableToTables(relatedRecord.records) };
		} else {
			tables[relatedRecord.recordType] = relatedRecord.records;
			return tables;
		}
	};

	mapMetaTableToTables = (
		metaTable: { typeName: string; displayName: string; typeCD: string }[]
	): { [table: string]: any[] } => {
		return metaTable.reduce(
			(a, c) => ({ ...a, [c.typeName]: [...(a[c.typeName] || []), this.mapMetaTableRowToRow(c)] }),
			{}
		);
	};

	mapMetaTableRowToRow = ({
		typeName,
		displayName,
		typeCD,
		...c
	}: {
		typeName: string;
		displayName: string;
		typeCD: string;
	}) => {
		const camelCaseTypeName = this.toCamelCase(typeName)
			.replace("outbuildingFloor", "floor")
			.replace("outbuildingConstruction", "construction");
		const codeSuffix = "TypeCD";
		const typeCDColumn = camelCaseTypeName + codeSuffix;
		const displayNameColumn = camelCaseTypeName + "Name";
		return {
			...c,
			[typeCDColumn]: typeCD,
			[displayNameColumn]: displayName,
		};
	};

	toCamelCase(str: string): string {
		return str
			.replace(/(?:^\w|[A-Z]|\b\w)/g, (ltr, idx) =>
				idx === 0 ? ltr.toLowerCase() : ltr.toUpperCase()
			)
			.replace(/\s+/g, "");
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingOptionsCacheBuster$,
		cacheHasher: (parameters) =>
			parameters[0] + "/" + parameters[1] + "/" + (parameters[2] as HttpParams).toString(),
		shouldCacheDecider: (results: any[]) => results?.length < 1000,
		maxAge: 60 * 60 * 1000,
		maxCacheCount: 99,
	})
	getFlattenedRows<T>(schema: string, table: string, httpParams: HttpParams): Observable<T[]> {
		if (!schema || !table) {
			return of([]);
		}
		return this.rest.get<T[]>([this.dynamicDataUrl, schema, table], httpParams).pipe(
			map((rows) => (rows || []).map((r) => this.cleanRow<T>(r))),
			map((rows) => (rows || []).sort(this.codeTableSortFn(table))),
			catchError((_) => of([]))
		);
	}

	cleanRow = <T>({
		modificationTimestampUTCDTTM,
		originalEntryTimestampUTCDTTM,
		...r
	}: {
		modificationTimestampUTCDTTM?: any;
		originalEntryTimestampUTCDTTM?: any;
	}): T => {
		return r as T;
	};

	@Cacheable({
		cacheBusterObserver: CacheBusters.codeTableCacheBuster$,
		cacheHasher: (parameters) =>
			parameters[0] + "/" + parameters[1] + "/" + (parameters[2] as HttpParams).toString(),
		shouldCacheDecider: (results: any[]) => results?.length < 100,
		maxAge: 24 * 60 * 60 * 1000,
		maxCacheCount: 999,
	})
	getCodeTableValues<T>(schema: string, table: string, httpParams: HttpParams): Observable<T[]> {
		return this.getFlattenedRows<T>(schema, table, httpParams);
	}

	getPropertyTypeCodeTableValues<T>(
		schema: string,
		table: string,
		httpParams: HttpParams,
		listingSummary: Pick<ListingSummary, "propertyTypeCD">
	): Observable<T[]> {
		const propertyTypeCD = getListingSummaryPropertyTypeCD(listingSummary);
		const columnName =
			propertyTypeCD === "CMU"
				? "commercialIND"
				: propertyTypeCD === "FRM"
				? "farmIND"
				: propertyTypeCD === "LL"
				? "landIND"
				: propertyTypeCD === "MF"
				? "multiFamilyIND"
				: propertyTypeCD === "RR"
				? "rentalIND"
				: "singleFamilyIND";
		return this.getCodeTableValues<T>(schema, table, httpParams).pipe(
			map((values) =>
				(values || []).filter(this.filterCodeTableValuesByPropertyTypeColumn(columnName))
			)
		);
	}

	filterCodeTableValuesByPropertyTypeColumn = (columnName: string) => (row: any) => {
		return !row?.hasOwnProperty?.(columnName) || row[columnName] === "Y";
	};

	getGenericProcResultSet<T, S = any[]>(httpParams: HttpParams): Observable<ApiResponse<T, S>> {
		return this.rest.get<ApiResponse<T, S>>(this.genericProcUrl, httpParams);
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingOptionsCacheBuster$,
		cacheHasher: (parameters) => parameters[0] + "/" + (parameters[1] as HttpParams).toString(),
		shouldCacheDecider: (results: any[]) => results?.length < 1000,
		maxAge: 60 * 60 * 1000,
		maxCacheCount: 99,
	})
	getGenericProcValues<T>(
		procName: string,
		httpParams: HttpParams,
		related?: RelatedRecord[]
	): Observable<T[]> {
		httpParams = httpParams.set("procName", procName);
		httpParams = httpParams.set("sinceDate", "1900-01-01 12:00:00");
		httpParams = httpParams.set("pageNumber", "1");
		httpParams = httpParams.set("pageSize", "500");
		return this.getGenericProcResultSet<T>(httpParams).pipe(
			map((response) => {
				if (related && related.length > 0 && response.related.length > 0) {
					this.findRelatedRecords(response, related);
				}
				return response.records || [];
			}),
			catchError((_) => of([]))
		);
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingOptionsCacheBuster$,
		cacheHasher: (parameters) => (parameters[0] as HttpParams).toString(),
		shouldCacheDecider: (results: any[]) => results?.length < 1000,
		maxAge: 60 * 60 * 1000,
		maxCacheCount: 99,
	})
	getSearchMemberResponse(httpParams: HttpParams): Observable<ApiResponse<Member>> {
		httpParams.set("withCredentials", true);
		return this.rest.get<ApiResponse<Member>>(this.searchingMemberUrl, httpParams);
	}

	getSearchMember(
		fullTextSearch: string | HttpParams,
		pageNumber = 1,
		pageSize = 100,
		sinceDate = "1900-01-01 12:00:00"
	): Observable<Member[]> {
		const fromObject = {
			pageNumber: pageNumber.toString(),
			pageSize: pageSize.toString(),
			sinceDate,
			fullTextSearch: "",
		};
		if (typeof fullTextSearch === "string") {
			fromObject.fullTextSearch = fullTextSearch;
		} else {
			fullTextSearch?.keys().forEach((key) => (fromObject[key] = fullTextSearch.get(key)));
		}
		const httpParams = new HttpParams({ fromObject });
		return this.getSearchMemberResponse(httpParams).pipe(map((response) => response.records));
	}

	getMember(
		query: HttpParams,
		pageNumber = 1,
		pageSize = 100,
		sinceDate = "1900-01-01 12:00:00"
	): Observable<Member[]> {
		const fromObject = {
			pageNumber: pageNumber.toString(),
			pageSize: pageSize.toString(),
			sinceDate,
		};
		query?.keys().forEach((key) => (fromObject[key] = query.get(key)));
		const httpParams = new HttpParams({ fromObject });
		return this.rest
			.get<ApiResponse<Member>>(this.memberUrl, httpParams)
			.pipe(map((response) => response.records));
	}

	getTaxData(
		listingSK: FK<Listing>,
		countyFips?: number,
		pid?: number
	): Observable<ApiPayload<ListingPropertyTax>> {
		const httpParams = new HttpParams({
			fromObject: {
				"county-fips": countyFips.toString(),
				pid: pid.toString(),
			},
		});
		return this.rest.get([this.taxDataUrl, listingSK], httpParams);
	}

	autopopListing(
		countyFips: string,
		listingClass: string,
		pid: string
	): Observable<ApiResponse<ListingSummary>> {
		const httpParams = new HttpParams({
			fromObject: {
				"county-fips": countyFips,
				"listing-class": listingClass,
				pid,
			},
		});
		return this.rest.post<ApiResponse<ListingSummary>>([
			this.autopopUrl + "?" + httpParams.toString(),
		]);
	}

	listingObserver = (
		successMessage: string,
		errorMessage: string,
		partialObserver?: PartialObserver<Listing>
	): PartialObserver<Listing> => {
		return {
			...partialObserver,
			complete: () => {
				if (!!successMessage) {
					this.alertService.alertSuccess(successMessage);
				}
				if (!!partialObserver?.complete) {
					partialObserver.complete();
				}
			},
			error: (err: any) => {
				if (!!errorMessage) {
					this.alertService.alertDanger(errorMessage);
				}
				if (!!partialObserver?.error) {
					partialObserver.error(err);
				}
			},
		};
	};

	deleteListingObserver = (
		listingSummary: ListingSummary,
		partialObserver?: PartialObserver<Listing>
	): PartialObserver<Listing> =>
		this.listingObserver(
			"Deleted listing #" + listingSummary.listingID,
			"Error deleting listing #" + listingSummary.listingID,
			partialObserver
		);

	recoverListingObserver = (
		listingSummary: ListingSummary,
		partialObserver?: PartialObserver<Listing>
	): PartialObserver<Listing> =>
		this.listingObserver(
			"Recovered listing #" + listingSummary.listingID,
			"Error recovering listing #" + listingSummary.listingID,
			partialObserver
		);

	updateListingStatusCD = (
		listingSummary: ListingSummary,
		listingStatusCD: ListingStatusCD
	): Observable<Listing> => {
		listingStatusCD = listingStatusCD || "I";
		return this.saveListing({
			recordType: "Listing",
			records: [
				{
					listingSK: listingSummary.listingSK,
					listingStatusCD,
					modificationTimestampUTCDTTM: listingSummary.modificationTimestampUTCDTTM,
				} as any,
			],
		}).pipe(
			map((response) => response?.records?.[0]),
			tap((_) => this.listingSummaryService.getByKey(String(listingSummary.listingID)))
		);
	};

	updateListingLatLng = (
		listingSummary: ListingSummary,
		latitude: number,
		longitude: number
	): Observable<ApiResponse<Listing, [any]>> => {
		const httpParams = new HttpParams({ fromObject: { latitude, longitude } });
		return this.rest.post<ApiResponse<Listing, [any]>>(
			[this.listingOperationsUrl, "map", listingSummary.listingSK],
			null,
			httpParams
		);
	};

	@CacheBuster({
		cacheBusterNotifier: CacheBusters.bulkListingsCacheBuster$,
	})
	deleteListing(listingSummary: ListingSummary): Observable<Listing> {
		const previousListingStatusCD: ListingStatusCD = listingSummary.listingStatusCD;
		let newListingStatus = "D";
		if (previousListingStatusCD === "I") {
			newListingStatus = "ID";
		} else if (previousListingStatusCD === "W") {
			newListingStatus = "WD";
		}
		return this.updateListingStatusCD(listingSummary, newListingStatus);
	}

	@CacheBuster({
		cacheBusterNotifier: CacheBusters.bulkListingsCacheBuster$,
	})
	recoverListing(listingSummary: ListingSummary): Observable<Listing> {
		let previousListingStatusCD: ListingStatusCD = listingSummary.previousListingStatusCD || "I";
		if (previousListingStatusCD === "D") {
			previousListingStatusCD = "I";
		}
		return this.updateListingStatusCD(listingSummary, previousListingStatusCD);
	}

	@CacheBuster({
		cacheBusterNotifier: CacheBusters.bulkListingsCacheBuster$,
	})
	copyListing(listingSK: ListingSummary["listingSK"]): Observable<ApiResponse<Listing>> {
		const httpParams = new HttpParams({
			fromObject: {
				listingSK: listingSK.toString(),
			},
		});
		return this.rest.get([this.addEditMediaUrl, "copy", listingSK], httpParams);
	}

	@CacheBuster({
		cacheBusterNotifier: CacheBusters.bulkListingsCacheBuster$,
	})
	cloneListing(listingSK: ListingSummary["listingSK"]): Observable<ApiResponse<Listing>> {
		const httpParams = new HttpParams({
			fromObject: {
				listingSK: listingSK.toString(),
			},
		});
		return this.rest.get([this.addEditMediaUrl, "clone", listingSK], httpParams);
	}

	@CacheBuster({
		cacheBusterNotifier: CacheBusters.bulkListingsCacheBuster$,
	})
	testListingImage(listingSK: number): Observable<ApiResponse<Listing>> {
		return this.rest.post([this.addEditMediaUrl, "test-listing", listingSK]);
	}

	getListingCity(params: HttpParams): Observable<any> {
		return this.rest.get([this.listingCityUrl], params);
	}

	possiblyAddTestImage(apiResponse, checkEnvironment = true) {
		const records = apiResponse && apiResponse.records[0] ? apiResponse.records[0] : null;
		const hasTestListingIndY = records ? records?.testListingIND === YesNo.YES : false;
		const canAddTestImage =
			(checkEnvironment && environment.name === LoonEnvironmentName.PROD) || !checkEnvironment
				? true
				: false;
		return hasTestListingIndY && canAddTestImage
			? this.testListingImage(records.listingSK).pipe(catchError(() => of(apiResponse)))
			: of(apiResponse);
	}

	private findRelatedRecords(response, related: RelatedRecord[]) {
		response.records.map((record) => {
			related.forEach((res) => {
				const findRecordType = response.related.find(
					(type) => res.recordType === type.recordType
				)?.records;

				if (findRecordType) {
					let findValue;
					res.params.forEach((param) => {
						Object.keys(param).forEach((key) => {
							findValue = findRecordType.find(
								(rel) => rel[res.key] === record[res.key] && rel[key] === param[key]
							);
						});
					});
					record[res.newProperty] = findValue?.[res.returnKey];
				}
			});
			return record;
		});
	}
}
